interface NodeWithChildren<T> {
  children: T[]
}

interface SectionWithNodes<T> {
  nodes: T[]
}

interface TagWithColor {
  id: string
  title: string
  colorHex?: string
}

export const sanitizeTagTitle = (value: string): string =>
  value.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')

export const normalizeTagTitle = (value: string): string =>
  sanitizeTagTitle(value).toLowerCase()

export const buildIdMapByKey = <T>({
  items,
  getKey,
  getId,
}: {
  items: T[]
  getKey: (item: T) => string | null | undefined
  getId: (item: T) => string
}): Map<string, string> => {
  const map = new Map<string, string>()

  items.forEach((item) => {
    const key = getKey(item)

    if (key && !map.has(key)) {
      map.set(key, getId(item))
    }
  })

  return map
}

export const buildTagIdByKey = <T extends { id: string; title: string }>(
  tags: T[]
): Map<string, string> =>
  buildIdMapByKey({
    items: tags,
    getKey: (tag) => normalizeTagTitle(tag.title),
    getId: (tag) => tag.id,
  })

export const mergeTagByTitle = ({
  tagsByKey,
  title,
  colorHex,
  id,
  createId,
}: {
  tagsByKey: Map<string, TagWithColor>
  title: string
  colorHex?: string
  id?: string
  createId: () => string
}): string | null => {
  const normalizedTitle = sanitizeTagTitle(title)
  const tagKey = normalizeTagTitle(normalizedTitle)

  if (!tagKey) {
    return null
  }

  const existingTag = tagsByKey.get(tagKey)

  if (!existingTag) {
    tagsByKey.set(tagKey, {
      id: id || createId(),
      title: normalizedTitle,
      ...(colorHex ? { colorHex } : {}),
    })
    return tagKey
  }

  if (!existingTag.colorHex && colorHex) {
    tagsByKey.set(tagKey, {
      ...existingTag,
      colorHex,
    })
  }

  return tagKey
}

export const walkSectionNodes = <T extends NodeWithChildren<T>>(
  sections: Array<SectionWithNodes<T>>,
  visitor: (node: T) => void
) => {
  const walkNode = (node: T) => {
    visitor(node)
    node.children.forEach(walkNode)
  }

  sections.forEach((section) => {
    section.nodes.forEach(walkNode)
  })
}

export const mapNodeTree = <T extends NodeWithChildren<T>, TResult>(
  nodes: T[],
  mapper: (node: T, mappedChildren: TResult[]) => TResult
): TResult[] => {
  const mapNode = (node: T): TResult =>
    mapper(
      node,
      node.children.map((childNode) => mapNode(childNode))
    )

  return nodes.map((node) => mapNode(node))
}
